import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileCsv } from 'react-icons/fa';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import samplePayslip from '../../assets/GSM-Payslips.csv';

const ImportPaySlip = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please choose a CSV file to import.');
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setFile(selectedFile);
    setSummary(null);
    setMessage('');
    setError('');
  };

  const handleImport = async (event) => {
    event.preventDefault();

    if (!file) {
      setError('Please choose a CSV file before importing.');
      setSummary(null);
      setMessage('');
      return;
    }

    setUploading(true);
    setError('');
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiRequest('/api/admin/payslips/import', {
        method: 'POST',
        body: formData,
      });

      setSummary(response.data ?? null);
      setMessage(response.message || 'Payslips imported successfully.');
    } catch (err) {
      setSummary(null);
      setError(err.message || 'Failed to import payslips.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setSummary(null);
    setMessage('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
          <div>
            <h4 className="mb-1">Import Pay Slips</h4>
            <p className="text-muted mb-0">Upload the monthly CSV to update employee payouts.</p>
          </div>
          <div className="d-flex gap-2">
            <a href={samplePayslip} download className="btn btn-outline-primary">
              Download Sample CSV
            </a>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/payslip')}
            >
              Back
            </button>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-7 col-xl-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <form onSubmit={handleImport}>
                  <div className="mb-4">
                    <label className="form-label">Upload CSV file</label>
                    <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        Choose File
                      </button>
                      <span className="text-muted small">Only .csv files are supported.</span>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="d-none"
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                    {file && (
                      <div className="mt-3 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <FaFileCsv className="text-success me-2" aria-hidden="true" />
                          <span>
                            {file.name}
                            <small className="text-muted ms-2">({Math.round(file.size / 1024)} KB)</small>
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger"
                          onClick={handleRemoveFile}
                          disabled={uploading}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="d-grid mb-3">
                    <button type="submit" className="btn btn-danger" disabled={uploading}>
                      {uploading ? 'Importing…' : 'Import Payslips'}
                    </button>
                  </div>

                  {message && (
                    <div className="alert alert-success" role="status">
                      {message}
                    </div>
                  )}

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}
                </form>

                {summary && (
                  <div className="mt-4">
                    <h6 className="mb-3">Import Summary</h6>
                    <div className="row g-3">
                      <div className="col-sm-6">
                        <div className="border rounded p-3 h-100">
                          <p className="mb-1 text-muted">Processed Rows</p>
                          <h5 className="mb-0">{summary.processed ?? 0}</h5>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="border rounded p-3 h-100">
                          <p className="mb-1 text-muted">Inserted</p>
                          <h5 className="mb-0 text-success">{summary.inserted ?? 0}</h5>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="border rounded p-3 h-100">
                          <p className="mb-1 text-muted">Updated</p>
                          <h5 className="mb-0 text-primary">{summary.updated ?? 0}</h5>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="border rounded p-3 h-100">
                          <p className="mb-1 text-muted">Skipped</p>
                          <h5 className="mb-0 text-warning">{summary.skipped ?? 0}</h5>
                        </div>
                      </div>
                    </div>

                    {Array.isArray(summary.errors) && summary.errors.length > 0 && (
                      <div className="mt-4">
                        <h6 className="mb-2">Sample Errors</h6>
                        <div className="alert alert-warning mb-0">
                          <p className="mb-2">First {summary.errors.length} issues detected:</p>
                          <ul className="mb-0 ps-3">
                            {summary.errors.map((item, index) => (
                              <li key={`${item.row ?? index}-${item.message}`}>
                                Row {item.row ?? '-'}: {item.message}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ImportPaySlip;

